package com.ijoin.ui.connect

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.ijoin.data.model.ConnectAccountStatus
import com.ijoin.data.repository.ConnectRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class ConnectViewModel(
    private val repository: ConnectRepository = ConnectRepository()
) : ViewModel() {

    private val _uiState = MutableStateFlow(ConnectUiState())
    val uiState: StateFlow<ConnectUiState> = _uiState.asStateFlow()

    init {
        loadStatus()
    }

    fun loadStatus() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            repository.getConnectStatus()
                .onSuccess { status ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            status = status,
                            error = null
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            error = error.message
                        )
                    }
                }
        }
    }

    fun startOnboarding(refreshUrl: String, returnUrl: String) {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            repository.createOnboardingLink(refreshUrl, returnUrl)
                .onSuccess { response ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            onboardingUrl = response.url
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            error = error.message
                        )
                    }
                }
        }
    }

    fun clearOnboardingUrl() {
        _uiState.update { it.copy(onboardingUrl = null) }
    }

    fun enableDirectPayments() {
        viewModelScope.launch {
            _uiState.update { it.copy(isSwitchingMode = true, error = null) }
            repository.enableDirectPayments()
                .onSuccess { status ->
                    _uiState.update {
                        it.copy(
                            isSwitchingMode = false,
                            status = status,
                            message = ConnectMessage.DirectPaymentsEnabled
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isSwitchingMode = false,
                            error = error.message
                        )
                    }
                }
        }
    }

    fun switchToPlatform() {
        viewModelScope.launch {
            _uiState.update { it.copy(isSwitchingMode = true, error = null) }
            repository.switchToPlatform()
                .onSuccess { status ->
                    _uiState.update {
                        it.copy(
                            isSwitchingMode = false,
                            status = status,
                            message = ConnectMessage.SwitchedToPlatform
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isSwitchingMode = false,
                            error = error.message
                        )
                    }
                }
        }
    }

    fun openDashboard() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            repository.getDashboardLink()
                .onSuccess { response ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            dashboardUrl = response.url
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            error = error.message
                        )
                    }
                }
        }
    }

    fun clearDashboardUrl() {
        _uiState.update { it.copy(dashboardUrl = null) }
    }

    fun clearMessage() {
        _uiState.update { it.copy(message = null) }
    }

    fun clearError() {
        _uiState.update { it.copy(error = null) }
    }
}

data class ConnectUiState(
    val isLoading: Boolean = true,
    val status: ConnectAccountStatus? = null,
    val error: String? = null,
    val onboardingUrl: String? = null,
    val dashboardUrl: String? = null,
    val isSwitchingMode: Boolean = false,
    val message: ConnectMessage? = null
)

sealed interface ConnectMessage {
    data object DirectPaymentsEnabled : ConnectMessage
    data object SwitchedToPlatform : ConnectMessage
}
